import React, { useEffect, useState, useMemo } from 'react';
import { listChallenges, createChallenge } from '../../lib/challenges';
import { Link, useNavigate } from 'react-router-dom';

export default function ChallengesList() {
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'scheduled' | 'ended'>('active');
  const [searchTerm, setSearchTerm] = useState('');
  const [duplicatingId, setDuplicatingId] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    console.log('[ChallengesList] Component mounted, loading challenges...');
    setLoading(true);
    setError(null);
    listChallenges()
      .then((data) => {
        console.log('[ChallengesList] Loaded challenges:', data);
        setItems(data);
      })
      .catch((err) => {
        console.error('[ChallengesList] Error loading challenges:', err);
        setError(err.message || 'Failed to load challenges');
      })
      .finally(() => {
        console.log('[ChallengesList] Loading complete');
        setLoading(false);
      });
  }, []);

  // Helper to get timestamp from Firestore timestamp or number
  const getTimestamp = (ts: any): number => {
    if (!ts) return 0;
    if (ts.toMillis) return ts.toMillis();
    if (typeof ts === 'number') return ts;
    return 0;
  };

  // Compute stats from items
  const stats = useMemo(() => {
    const now = Date.now();
    let activeCount = 0;
    let scheduledCount = 0;
    let endedCount = 0;
    let totalCompletions = 0;
    let totalPixPaid = 0;

    items.forEach((c) => {
      const startAt = getTimestamp(c.startAt);
      const endAt = getTimestamp(c.endAt);
      const analytics = c.analytics || {};

      // Active: status "active" AND within start/end window
      if (c.status === 'active' && startAt <= now && endAt >= now) {
        activeCount++;
      }
      // Scheduled: startDate in the future OR explicit "scheduled" status
      else if (c.status === 'scheduled' || (startAt > now && c.status === 'active')) {
        scheduledCount++;
      }
      // Ended: endDate in the past OR explicit "ended" status
      if (endAt < now || c.status === 'ended') {
        endedCount++;
      }

      totalCompletions += analytics.completionsCount || 0;
      totalPixPaid += analytics.pixPaidTotal || 0;
    });

    return {
      active: activeCount,
      scheduled: scheduledCount,
      ended: endedCount,
      totalCompletions,
      totalPixPaid
    };
  }, [items]);

  // Filter challenges
  const filteredChallenges = useMemo(() => {
    const now = Date.now();
    return items.filter((c) => {
      // Status filter
      const startAt = getTimestamp(c.startAt);
      const endAt = getTimestamp(c.endAt);
      let matchesStatus = false;

      if (statusFilter === 'all') {
        matchesStatus = true;
      } else if (statusFilter === 'active') {
        matchesStatus = c.status === 'active' && startAt <= now && endAt >= now;
      } else if (statusFilter === 'scheduled') {
        matchesStatus = c.status === 'scheduled' || (startAt > now && c.status === 'active');
      } else if (statusFilter === 'ended') {
        matchesStatus = endAt < now || c.status === 'ended';
      }

      // Search filter
      const matchesSearch = !searchTerm || 
        c.title?.toLowerCase().includes(searchTerm.toLowerCase());

      return matchesStatus && matchesSearch;
    });
  }, [items, statusFilter, searchTerm]);

  // Duplicate challenge
  const handleDuplicate = async (challenge: any) => {
    if (duplicatingId) return; // Prevent double-clicks
    
    setDuplicatingId(challenge.id);
    try {
      const now = Date.now();
      const oneDayFromNow = now + 24 * 60 * 60 * 1000;
      const sevenDaysFromNow = now + 7 * 24 * 60 * 60 * 1000;

      const newChallenge = {
        title: `${challenge.title} (Copy)`,
        imageUrl: challenge.imageUrl || '',
        startAt: oneDayFromNow,
        endAt: sevenDaysFromNow,
        status: 'scheduled' as const,
        requiredCount: challenge.requiredCount || 10,
        pixReward: challenge.pixReward || 1000,
        requireUniqueTasks: challenge.requireUniqueTasks || false,
        eligible: challenge.eligible || { categories: [], subcategories: [], taskIds: [] }
      };

      const newId = await createChallenge(newChallenge);
      navigate(`/challenges/${newId}`);
    } catch (err) {
      console.error('[ChallengesList] Error duplicating challenge:', err);
      alert('Failed to duplicate challenge. Please try again.');
    } finally {
      setDuplicatingId(null);
    }
  };

  console.log('[ChallengesList] Render - loading:', loading, 'error:', error, 'items:', items.length);

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Challenges</h1>
        <Link to="/challenges/new" className="px-3 py-2 bg-black text-white rounded">New Challenge</Link>
      </div>

      {/* Stats Header */}
      {!loading && !error && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="bg-white p-4 rounded-lg border">
            <div className="text-2xl font-bold text-green-600">{stats.active}</div>
            <div className="text-sm text-gray-600">Active Challenges</div>
          </div>
          <div className="bg-white p-4 rounded-lg border">
            <div className="text-2xl font-bold text-blue-600">{stats.scheduled}</div>
            <div className="text-sm text-gray-600">Scheduled Challenges</div>
          </div>
          <div className="bg-white p-4 rounded-lg border">
            <div className="text-2xl font-bold text-gray-600">{stats.ended}</div>
            <div className="text-sm text-gray-600">Ended Challenges</div>
          </div>
          <div className="bg-white p-4 rounded-lg border">
            <div className="text-2xl font-bold text-purple-600">{stats.totalCompletions}</div>
            <div className="text-sm text-gray-600">Total Completions</div>
          </div>
          <div className="bg-white p-4 rounded-lg border">
            <div className="text-2xl font-bold text-orange-600">{stats.totalPixPaid.toLocaleString()}</div>
            <div className="text-sm text-gray-600">Total PIX Paid</div>
          </div>
        </div>
      )}

      {/* Status Filters + Search Bar */}
      {!loading && !error && (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            {/* Status Filter Buttons */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setStatusFilter('all')}
                className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                  statusFilter === 'all'
                    ? 'bg-black text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setStatusFilter('active')}
                className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                  statusFilter === 'active'
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Active
              </button>
              <button
                onClick={() => setStatusFilter('scheduled')}
                className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                  statusFilter === 'scheduled'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Scheduled
              </button>
              <button
                onClick={() => setStatusFilter('ended')}
                className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                  statusFilter === 'ended'
                    ? 'bg-gray-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Ended
              </button>
            </div>

            {/* Search Bar */}
            <div className="flex-1 md:max-w-md">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search challenges…"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>
      )}

      {loading && <div className="text-gray-500">Loading challenges...</div>}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded text-red-700">
          <div className="font-semibold">Error loading challenges</div>
          <div className="text-sm mt-1">{error}</div>
          <div className="text-xs mt-2 text-red-600">
            {error.includes('index') && 'Note: You may need to create a Firestore composite index for challenges collection (status, endAt)'}
          </div>
        </div>
      )}
      {!loading && !error && filteredChallenges.length === 0 && (
        <div className="text-gray-500 p-4">No challenges found{searchTerm || statusFilter !== 'all' ? ' matching your filters' : ''}. Create your first challenge!</div>
      )}
      {!loading && !error && filteredChallenges.length > 0 && (
        <div className="grid gap-3">
          {filteredChallenges.map((c) => {
            const analytics = c.analytics || {};
            const isDuplicating = duplicatingId === c.id;
            
            return (
              <div key={c.id} className="border rounded p-4 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <Link to={`/challenges/${c.id}`} className="flex-1">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-semibold">{c.title}</div>
                        <div className="text-sm text-gray-500">
                          {c.status} · Ends {new Date(getTimestamp(c.endAt)).toLocaleString()}
                        </div>
                      </div>
                      <div className="text-sm text-gray-600">
                        Req: {c.requiredCount} · Reward: +{c.pixReward} PIX
                      </div>
                    </div>
                  </Link>
                  
                  {/* Stats and Duplicate Button */}
                  <div className="flex items-center gap-4 ml-4">
                    <div className="text-right text-sm text-gray-600">
                      <div>Completions: {analytics.completionsCount || 0}</div>
                      <div>PIX Paid: {analytics.pixPaidTotal || 0}</div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleDuplicate(c);
                      }}
                      disabled={isDuplicating}
                      className={`px-3 py-1 text-sm rounded transition-colors ${
                        isDuplicating
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                      title="Duplicate this challenge"
                    >
                      {isDuplicating ? 'Duplicating...' : 'Duplicate'}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}



